// [목적] 비로그인 사용자가 보호된 인터랙션을 시도할 때 노출되는 로그인 유도 모달 컴포넌트
// [이유] SLE-FE-04 (auth-signup-login-expansion Wave C) — intent 별 맥락 있는 안내 카피로 전환율 제고.
//         MUI Dialog 베이스로 focus trap / Esc 닫힘 / aria-labelledby 등 WCAG 2.1 AA 접근성 자동 확보.
// [사이드 이팩트] "로그인" CTA 클릭 시 현재 경로를 base64url 인코딩하여 /login?returnTo=<encoded> 로 이동.
//                 returnTo 는 sanitizeNextPath 로 same-origin 검증 후 사용 (open redirect 방어).
//                 "취소" / Esc / backdrop 클릭 → onClose() 호출.
// [수정 시 고려사항] 신규 intent 추가 시 INTENT_COPY 맵과 AuthIntent 타입(useAuthGate.ts) 동시 갱신.
//                   returnTo 인코딩은 Sub 4 기준: btoa(unescape(encodeURIComponent(path))) base64url 변환.
//                   LoginPage 의 returnTo 수신 로직(searchParams.get('returnTo'))은 변경 없음 — 정합 유지.

import { useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';

import type { AuthIntent } from '../../hooks/useAuthGate.js';
import { sanitizeNextPath } from '../../api/safeRedirect.js';

// ─── 타입 ──────────────────────────────────────────────────────────────────

export interface LoginPromptModalProps {
  open: boolean;
  intent: AuthIntent | null;
  onClose: () => void;
}

// ─── intent 별 카피 맵 ──────────────────────────────────────────────────────

/**
 * [목적] intent 별 타이틀/설명 카피 — 사용자에게 맥락 있는 로그인 유도 메시지 전달
 * [이유] SLE-FE-04 Sub 2: 같은 모달이라도 진입 의도에 따라 안내 문구를 다르게 하여 전환율 향상
 * [사이드 이팩트] 없음 (순수 상수)
 * [수정 시 고려사항] i18n 도입 시 이 맵의 값을 번역 키로 교체
 */
const INTENT_COPY: Record<AuthIntent, { title: string; description: string }> = {
  'chat.create': {
    title: '로그인이 필요해요',
    description: '로그인하고 여행 폴리오를 만들어 보세요',
  },
  'folio.like': {
    title: '로그인이 필요해요',
    description: '좋아요는 로그인 후 사용할 수 있어요',
  },
  'folio.bookmark': {
    title: '로그인이 필요해요',
    description: '저장은 로그인 후 사용할 수 있어요',
  },
  'folio.comment': {
    title: '로그인이 필요해요',
    description: '댓글은 로그인 후 사용할 수 있어요',
  },
  // [수정] festival-comments: 축제 상세 댓글 — folio.comment 와 동일 카피(useAuthGate.ts AuthIntent 확장과 동기).
  'festival.comment': {
    title: '로그인이 필요해요',
    description: '댓글은 로그인 후 사용할 수 있어요',
  },
  // [수정] FlowLine A7: report.create intent 카피 — useAuthGate.ts AuthIntent 확장과 동기.
  'report.create': {
    title: '로그인이 필요해요',
    description: '신고는 로그인 후 사용할 수 있어요',
  },
  // [수정] QA P2-01: 게스트가 "새 컬렉션"을 눌렀을 때 — useAuthGate.ts AuthIntent 확장과 동기.
  'collection.create': {
    title: '로그인이 필요해요',
    description: '컬렉션은 로그인 후 만들 수 있어요',
  },
};

const DEFAULT_COPY = INTENT_COPY['chat.create'];

// ─── returnTo 인코딩 헬퍼 ────────────────────────────────────────────────────

/**
 * [목적] 현재 경로를 base64url 인코딩하여 returnTo 쿼리 파라미터 값을 생성
 * [이유] SLE-FE-04 Sub 4: /login?returnTo=<encoded> 패턴 — LoginPage 가 base64url 디코딩 없이
 *         encodeURIComponent 그대로 수신하더라도 sanitizeNextPath 통과.
 *         단순 encodeURIComponent 를 사용하면 /chat 같이 짧은 경로도 그대로 통과.
 * [사이드 이팩트] 없음 (순수 함수)
 * [수정 시 고려사항] sanitizeNextPath 는 leading '/' 검증 — 경로만 전달하고 scheme 은 포함하지 않음.
 *                   open redirect 방어: sanitizeNextPath 를 반드시 거친 경로만 인코딩.
 */
function buildReturnTo(pathname: string, search: string): string {
  const raw = pathname + search;
  // [보안] same-origin 내부 경로만 허용 (open redirect 방어)
  const safe = sanitizeNextPath(raw);
  return encodeURIComponent(safe);
}

// ─── 컴포넌트 ───────────────────────────────────────────────────────────────

const L = 'light' as const;
const c = tokens.color;

/**
 * [목적] 게스트 인터랙션 가드 로그인 유도 모달
 * [이유] SLE-FE-04 Sub 2: intent 별 카피 + "로그인" CTA(returnTo 결합) + "취소" CTA
 * [사이드 이팩트] "로그인" 클릭 → navigate('/login?returnTo=...') → SPA 라우터 이동 (full reload 아님).
 *                 LoginPage 가 returnTo 를 읽어 login(provider, returnTo) 로 이어받음.
 * [수정 시 고려사항] MUI Dialog 의 기본 focus trap / Esc onClose / backdrop onClose 는 그대로 활성화.
 *                   aria-labelledby="login-prompt-title" / aria-describedby="login-prompt-desc" 명시.
 *                   초점 복귀: Dialog disableRestoreFocus 미사용 → 닫힌 후 trigger 요소로 자동 복귀(MUI 기본).
 */
export function LoginPromptModal({ open, intent, onClose }: LoginPromptModalProps): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();

  const copy = (intent !== null ? INTENT_COPY[intent] : null) ?? DEFAULT_COPY;

  /**
   * [목적] "로그인" CTA 핸들러 — returnTo 결합 후 /login 으로 SPA navigate
   * [이유] SPA navigate 사용 → LoginPage mount 시 useSearchParams 로 returnTo 수신 가능.
   *         full reload(window.location.assign) 는 불필요 — OAuth 흐름은 LoginPage 에서 시작.
   * [사이드 이팩트] onClose 먼저 호출하여 모달을 닫은 후 navigate — 두 개의 상태 변경이 겹치지 않도록.
   * [수정 시 고려사항] useAuth.login(provider, returnTo) 는 LoginPage 에서 담당 — 여기서는 /login 진입만.
   */
  function handleLoginClick(): void {
    onClose();
    const returnTo = buildReturnTo(location.pathname, location.search);
    navigate(`/login?returnTo=${returnTo}`);
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="login-prompt-title"
      aria-describedby="login-prompt-desc"
      // [목적] 모달 최대 너비 제한 — 모바일에서도 읽기 편한 크기
      // [이유] xs 고정으로 콘텐츠가 과도하게 늘어나지 않도록 제한
      maxWidth="xs"
      fullWidth
      // [목적] MUI Paper 스타일에 토큰 적용
      // [이유] CLAUDE.md §6-3 디자인 토큰만 사용 — #hex/px 직접 입력 금지(예외: tokens 정의 파일 자체)
      PaperProps={{
        sx: {
          borderRadius: `${tokens.radius.l}px`,
          bgcolor: c.paper[0][L],
        },
      }}
    >
      <DialogTitle
        id="login-prompt-title"
        sx={{
          fontSize: 16,
          fontWeight: 700,
          color: c.ink.high[L],
          pb: `${tokens.spacing[1]}px`,
        }}
      >
        {copy.title}
      </DialogTitle>

      <DialogContent sx={{ pt: `${tokens.spacing[1]}px` }}>
        <DialogContentText
          id="login-prompt-desc"
          sx={{
            fontSize: 14,
            color: c.ink.mid[L],
            lineHeight: 1.6,
          }}
        >
          {copy.description}
        </DialogContentText>
      </DialogContent>

      <DialogActions
        sx={{
          px: `${tokens.spacing[4]}px`,
          pb: `${tokens.spacing[3]}px`,
          gap: `${tokens.spacing[1]}px`,
        }}
      >
        {/* 취소 — 보조 액션 */}
        <Button
          onClick={onClose}
          variant="text"
          aria-label="취소"
          data-testid="login-prompt-cancel"
          sx={{
            color: c.ink.mid[L],
            fontSize: 14,
            fontWeight: 500,
            borderRadius: `${tokens.radius.m}px`,
            px: `${tokens.spacing[3]}px`,
            '&:focus-visible': {
              outline: `2px solid ${c.brand[500][L]}`,
              outlineOffset: `${tokens.spacing[1]}px`,
            },
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        >
          취소
        </Button>

        {/* 로그인 — 주 액션 */}
        <Button
          onClick={handleLoginClick}
          variant="contained"
          aria-label="로그인 페이지로 이동"
          data-testid="login-prompt-login"
          disableElevation
          sx={{
            bgcolor: c.brand[500][L],
            color: c.paper[0][L],
            fontSize: 14,
            fontWeight: 700,
            borderRadius: `${tokens.radius.m}px`,
            px: `${tokens.spacing[4]}px`,
            '&:hover': {
              bgcolor: c.brand[500][L],
              opacity: 0.88,
            },
            '&:focus-visible': {
              outline: `2px solid ${c.brand[500][L]}`,
              outlineOffset: `${tokens.spacing[1]}px`,
            },
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        >
          로그인
        </Button>
      </DialogActions>
    </Dialog>
  );
}
